import { FavouriteChannelsTray } from '../../../domain/favourite-channels-tray';
import { LiveTVRemindersTray } from '../../../domain/live-tv-reminders-tray';
import { ContinueWatchingTray } from '../../../domain/continue-watching-tray';
import { MyListTray } from '../../../domain/my-list';
import { TVGuideTray } from '../../../domain/tv-guide-tray';
import {
  BuiltInPageItemApiModel,
  BuiltInTypeApiModel,
  CreateBuiltInPageItemRequestApiModel,
  UpdateBuiltInPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  displayServiceTypeIconMapperDomainToApi,
  nameLocalizedMapperDomainToApi,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { BuiltInItemDomainModel, DomainBuiltInItemType } from '../../../domain/builtIn';
import {
  commonMapper,
  displayServiceTypeIconMapperApiToDomain,
  dthStatusMapper,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { FavouriteChannelsTrayFormViewModel } from '../favourite-channels-tray-form/favourite-channels-tray-form.view-model';
import { LiveTVRemindersTrayFormViewModel } from '../live-tv-reminders-tray-form/live-tv-reminders-tray-form.view-model';
import { TVGuideTrayFormViewModel } from '../tv-guide-tray-form/tv-guide-tray-form.view-model';
import { MyListTrayFormViewModel } from '../my-list-tray-form/my-list-tray-form.view-model';
import { ContinueWatchingTrayFormViewModel } from '../continue-watching-tray-form/continue-watching-tray-form.view-model';
import { BuiltInItemFormViewModel } from './built-in-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  displayServiceTypeIconMapper,
  nameLocalizedMapperDomainToViewModel,
  platformMapper,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  commonMapperViewModelToDomain,
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';
import { WatchlistOnDemandTray } from '../../../domain/watchlist-ondemand-tray';
import { WatchlistOnDemandTrayFormViewModel } from '../watchlist-ondemand-tray-form/watchlist-ondemand-tray-form.view-model';
import { ContinueWatchingOndemandTray } from '../../../domain/continue-watching-ondemand-tray';
import { ContinueWatchingOndemandTrayViewModel } from '../continue-watching-ondemand-tray/continue-watching-ondemand-tray.view-model';
import { PrimeVideoRecommendationsTray } from '../../../domain/prime-video-recommendations-tray';
import { PrimeVideoRecommendationsTrayFormViewModel } from '../prime-video-recommendations-tray-form/prime-video-recommendations-tray-form.view-model';

// 'BuiltIn'

/**
 * DOMAIN -> API
 */

const mapDomainBuiltInTypeApi = (builtInType: DomainBuiltInItemType): BuiltInTypeApiModel => {
  switch (builtInType) {
    case DomainBuiltInItemType.Watchlist:
      return BuiltInTypeApiModel.Watchlist; // .WatchlistCarousel;
    case DomainBuiltInItemType.ContinueWatching:
      return BuiltInTypeApiModel.ContinueWatching; // .ContinueWatchingCarousel;
    case DomainBuiltInItemType.WatchlistCarousel:
      return BuiltInTypeApiModel.WatchlistCarousel;
    case DomainBuiltInItemType.ContinueWatchingCarousel:
      return BuiltInTypeApiModel.ContinueWatchingCarousel;
    case DomainBuiltInItemType.WatchlistOnDemand:
      return BuiltInTypeApiModel.OndemandWatchlist;
    case DomainBuiltInItemType.ContinueWatchingOndemand:
      return BuiltInTypeApiModel.OndemandContinueWatching;
    case DomainBuiltInItemType.PrimeVideoRecommendations:
      return BuiltInTypeApiModel.PrimeVideoPlaceHolder;
    default:
      return undefined;
  }
};

export const mapBuiltInTrayDomainModelToApiModel = (
  item:
    | FavouriteChannelsTray
    | LiveTVRemindersTray
    | ContinueWatchingTray
    | MyListTray
    | TVGuideTray
    | ContinueWatchingOndemandTray
    | WatchlistOnDemandTray
    | PrimeVideoRecommendationsTray,
  pageVersionId?: string,
  tenantId?: string
): CreateBuiltInPageItemRequestApiModel & UpdateBuiltInPageItemRequestApiModel => {
  const commonProperties = mapperPipe(
    item,
    nameLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    displayServiceTypeIconMapperDomainToApi,
    scheduledDateMapperDomainToApi,
    platformTargetingMapper
  );

  const createData: CreateBuiltInPageItemRequestApiModel = remainderMapper(commonProperties, () => ({
    pageVersionId,
    builtInType: undefined,
    tenantId,
  }));

  switch (item.type) {
    case 'MyFavouritesOnLiveTV': // AXB-71
      createData.builtInType = BuiltInTypeApiModel.FavouriteChannels;
      break;
    case 'LiveTVReminders': // AXB-84
      createData.builtInType = BuiltInTypeApiModel.YourReminders;
      break;
    case 'ContinueWatchingTray': // AXB-79
      createData.builtInType = BuiltInTypeApiModel.ContinueWatching;
      createData.cardBackgroundImage = (item as ContinueWatchingTray).backgroundImage.BIGSCREEN.en.id;
      break;
    case 'MyListTray': // AXB-80
      createData.builtInType = BuiltInTypeApiModel.Watchlist;
      createData.cardBackgroundImage = (item as MyListTray).backgroundImage.BIGSCREEN.en.id;
      break;
    case 'TVGuide': // AXB-78
      createData.builtInType = BuiltInTypeApiModel.TvGuide;
      break;
    case 'WatchlistOnDemandTray':
      createData.builtInType = BuiltInTypeApiModel.OndemandWatchlist;
      createData.layout = (item as WatchlistOnDemandTray).layout;
      break;
    case 'PrimeVideoRecommendationsTray':
      createData.builtInType = BuiltInTypeApiModel.PrimeVideoPlaceHolder;
      createData.layout = (item as PrimeVideoRecommendationsTray).layout;
      break;
    case 'ContinueWatchingOndemandTray':
      createData.builtInType = BuiltInTypeApiModel.OndemandContinueWatching;
      createData.layout = (item as ContinueWatchingOndemandTray).layout;
  }

  return createData;
};

export const mapDomainBuiltInToApi = (
  builtInItem: BuiltInItemDomainModel,
  pageVersionId: string,
  tenantId?: string
): CreateBuiltInPageItemRequestApiModel => {
  const commonProperties = mapperPipe(builtInItem, nameLocalizedMapperDomainToApi, targetingMapperDomainToApi, platformTargetingMapper);

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    builtInType: mapDomainBuiltInTypeApi(builtInItem.builtInType),
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */

const mapApiBuiltInTypeToDomain = (
  builtInType: BuiltInTypeApiModel
):
  | DomainBuiltInItemType.FavouriteChannels
  | DomainBuiltInItemType.YourReminders
  | DomainBuiltInItemType.ContinueWatching
  | DomainBuiltInItemType.Watchlist
  | DomainBuiltInItemType.TVGuide
  | DomainBuiltInItemType.WatchlistCarousel
  | DomainBuiltInItemType.ContinueWatchingCarousel
  | DomainBuiltInItemType.WatchlistOnDemand
  | DomainBuiltInItemType.PrimeVideoRecommendations
  | DomainBuiltInItemType.ContinueWatchingOndemand => {
  switch (builtInType) {
    case BuiltInTypeApiModel.FavouriteChannels:
      return DomainBuiltInItemType.FavouriteChannels;
    case BuiltInTypeApiModel.YourReminders:
      return DomainBuiltInItemType.YourReminders;
    case BuiltInTypeApiModel.ContinueWatching:
      return DomainBuiltInItemType.ContinueWatching;
    case BuiltInTypeApiModel.Watchlist:
      return DomainBuiltInItemType.Watchlist;
    case BuiltInTypeApiModel.TvGuide:
      return DomainBuiltInItemType.TVGuide;
    case BuiltInTypeApiModel.WatchlistCarousel:
      return DomainBuiltInItemType.WatchlistCarousel;
    case BuiltInTypeApiModel.ContinueWatchingCarousel:
      return DomainBuiltInItemType.ContinueWatchingCarousel;
    case BuiltInTypeApiModel.OndemandWatchlist:
      return DomainBuiltInItemType.WatchlistOnDemand;
    case BuiltInTypeApiModel.PrimeVideoPlaceHolder:
      return DomainBuiltInItemType.PrimeVideoRecommendations;
    case BuiltInTypeApiModel.OndemandContinueWatching:
      return DomainBuiltInItemType.ContinueWatchingOndemand;
  }
};

export const mapBuiltInTrayApiModelToDomainModel = (builtIn: BuiltInPageItemApiModel): BuiltInItemDomainModel => {
  return remainderMapper(
    mapperPipe(
      builtIn,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      displayServiceTypeIconMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      dthStatusMapper
    ),
    () => ({
      type: 'BuiltIn',
      cardBackgroundImage: builtIn.cardBackgroundImage,
      builtInType: mapApiBuiltInTypeToDomain(builtIn.builtInType),
      layout: builtIn?.layout,
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

const mapDomainBuiltInTypeToViewModelBuiltInType = (
  itemType: DomainBuiltInItemType
):
  | TrayFormViewModelType.MyFavouritesOnLiveTV
  | TrayFormViewModelType.LiveTVReminders
  | TrayFormViewModelType.ContinueWatchingTray
  | TrayFormViewModelType.MyListTray
  | TrayFormViewModelType.TVGuide
  | TrayFormViewModelType.WatchlistCarousel
  | TrayFormViewModelType.ContinueWatchingCarousel
  | TrayFormViewModelType.WatchlistOnDemandTray
  | TrayFormViewModelType.PrimeVideoRecommendationsTray
  | TrayFormViewModelType.ContinueWatchingOndemandTray => {
  switch (itemType) {
    case DomainBuiltInItemType.FavouriteChannels:
      return TrayFormViewModelType.MyFavouritesOnLiveTV; // AXB-71
    case DomainBuiltInItemType.YourReminders:
      return TrayFormViewModelType.LiveTVReminders; // AXB-84
    case DomainBuiltInItemType.ContinueWatching:
      return TrayFormViewModelType.ContinueWatchingTray; // AXB-79
    case DomainBuiltInItemType.Watchlist:
      return TrayFormViewModelType.MyListTray; // AXB-80
    case DomainBuiltInItemType.TVGuide:
      return TrayFormViewModelType.TVGuide; // AXB-78
    case DomainBuiltInItemType.WatchlistCarousel:
      return TrayFormViewModelType.WatchlistCarousel;
    case DomainBuiltInItemType.ContinueWatchingCarousel:
      return TrayFormViewModelType.ContinueWatchingCarousel;
    case DomainBuiltInItemType.WatchlistOnDemand:
      return TrayFormViewModelType.WatchlistOnDemandTray;
    case DomainBuiltInItemType.PrimeVideoRecommendations:
      return TrayFormViewModelType.PrimeVideoRecommendationsTray;
    case DomainBuiltInItemType.ContinueWatchingOndemand:
      return TrayFormViewModelType.ContinueWatchingOndemandTray;
  }
};

export const mapBuiltInTrayDomainModelToViewModel = (
  builtInItem: BuiltInItemDomainModel
):
  | FavouriteChannelsTrayFormViewModel
  | LiveTVRemindersTrayFormViewModel
  | TVGuideTrayFormViewModel
  | MyListTrayFormViewModel
  | ContinueWatchingTrayFormViewModel
  | BuiltInItemFormViewModel
  | WatchlistOnDemandTrayFormViewModel
  | PrimeVideoRecommendationsTrayFormViewModel
  | ContinueWatchingOndemandTrayViewModel => {
  const type = mapDomainBuiltInTypeToViewModelBuiltInType(builtInItem.builtInType);

  const commonProperties = mapperPipe(
    builtInItem,
    commonMapper,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel,
    displayServiceTypeIconMapper,
    platformMapper
  );

  if (type === TrayFormViewModelType.MyListTray || type === TrayFormViewModelType.ContinueWatchingTray) {
    return remainderMapper(commonProperties, () => ({
      type,
      backgroundImage: builtInItem.cardBackgroundImage
        ? {
            [PlatformType.BigScreen]: {
              en: {
                id: builtInItem.cardBackgroundImage.id,
                name: builtInItem.cardBackgroundImage.originalName,
                publicUrl: builtInItem.cardBackgroundImage.publicUrl,
              },
            },
          }
        : undefined,
    }));
  }

  return remainderMapper(commonProperties, () => ({
    type,
    layout: builtInItem?.layout,
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

const mapViewModelBuiltInItemTypeToDomainType = (
  itemType:
    | TrayFormViewModelType.ContinueWatchingCarousel
    | TrayFormViewModelType.ContinueWatchingTray
    | TrayFormViewModelType.WatchlistCarousel
    | TrayFormViewModelType.MyListTray
    | TrayFormViewModelType.WatchlistOnDemandTray
    | TrayFormViewModelType.PrimeVideoRecommendationsTray
    | TrayFormViewModelType.ContinueWatchingOndemandTray
):
  | DomainBuiltInItemType.Watchlist
  | DomainBuiltInItemType.ContinueWatching
  | DomainBuiltInItemType.WatchlistCarousel
  | DomainBuiltInItemType.ContinueWatchingCarousel
  | DomainBuiltInItemType.WatchlistOnDemand
  | DomainBuiltInItemType.PrimeVideoRecommendations
  | DomainBuiltInItemType.ContinueWatchingOndemand => {
  switch (itemType) {
    case TrayFormViewModelType.WatchlistCarousel: // CXB
      return DomainBuiltInItemType.WatchlistCarousel;
    case TrayFormViewModelType.ContinueWatchingCarousel: // CXB
      return DomainBuiltInItemType.ContinueWatchingCarousel;
    case TrayFormViewModelType.MyListTray: // UXB
      return DomainBuiltInItemType.Watchlist;
    case TrayFormViewModelType.ContinueWatchingTray: // UXB
      return DomainBuiltInItemType.ContinueWatching;
    case TrayFormViewModelType.WatchlistOnDemandTray:
      return DomainBuiltInItemType.WatchlistOnDemand;
    case TrayFormViewModelType.PrimeVideoRecommendationsTray:
      return DomainBuiltInItemType.PrimeVideoRecommendations;
    case TrayFormViewModelType.ContinueWatchingOndemandTray:
      return DomainBuiltInItemType.ContinueWatchingOndemand;
    default:
      return undefined;
  }
};

export const mapBuiltInTrayFormViewModelToDomainModel = (
  item: BuiltInItemFormViewModel,
  loggedInUserSegmentIds: string[]
): BuiltInItemDomainModel => {
  return {
    id: item.id || '',
    titleEnglish: item?.title?.en ?? '',
    titleArabic: item?.title?.ar ?? '',
    userSegmentIds: loggedInUserSegmentIds || [],
    builtInType: mapViewModelBuiltInItemTypeToDomainType(item.type),
    type: 'BuiltIn',
    order: undefined,
    visible: undefined,
    draggable: true,
    countryIds: item.countryIds.slice(),
    platformIds: [...item?.platformIds],
  } as BuiltInItemDomainModel;
};

export const mapFavouriteChannelsTrayViewModelToDomainModel = (vm: FavouriteChannelsTrayFormViewModel): FavouriteChannelsTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'MyFavouritesOnLiveTV', // AXB-71
  }));
};

export const mapLiveTVRemindersTrayViewModelToDomainModel = (vm: LiveTVRemindersTrayFormViewModel): LiveTVRemindersTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'LiveTVReminders',
  }));
};

export const mapTVGuideTrayViewModelToDomainModel = (vm: TVGuideTrayFormViewModel): TVGuideTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'TVGuide',
  }));
};

export const mapMyListTrayViewModelToDomainModel = (vm: MyListTrayFormViewModel): MyListTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'MyListTray', // AXB-80
    backgroundImage: vm.backgroundImage,
  }));
};

export const mapContinueWatchingTrayViewModelToDomainModel = (vm: ContinueWatchingTrayFormViewModel): ContinueWatchingTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'ContinueWatchingTray', // AXB-79
    backgroundImage: vm.backgroundImage,
  }));
};

export const mapWatchlistOnDemandTrayViewModelToDomainModel = (vm: WatchlistOnDemandTrayFormViewModel): WatchlistOnDemandTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'WatchlistOnDemandTray', // MYX-85
    layout: vm.layout,
  }));
};

export const mapPrimeVideoRecommendationsTrayViewModelToDomainModel = (
  vm: PrimeVideoRecommendationsTrayFormViewModel
): PrimeVideoRecommendationsTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'PrimeVideoRecommendationsTray',
    layout: vm.layout,
  }));
};

export const mapContinueWatchingOndemandTrayViewModelToDomainModel = (
  vm: ContinueWatchingOndemandTrayViewModel
): ContinueWatchingOndemandTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'ContinueWatchingOndemandTray', // MYX-86
    layout: vm.layout,
  }));
};
